/**
 * useGhcpModels Hook
 *
 * Fetches available models for a GitHub Copilot agent connection by calling
 * the runtime directly (via buildRuntimeUrl). The runtime acquires a GHCP
 * token from CRP (s2s broker key) and proxies to api.githubcopilot.com/models.
 *
 * On failure the dropdown is empty but the combobox still accepts freeform
 * input (same behaviour as OpenAI).
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useRuntimeDetails } from '@/hooks/useAppDetails';
import { listConnectionModels, type GhcpModel } from '@/services/connectionApi';

// =============================================================================
// HOOK
// =============================================================================

export interface UseGhcpModelsReturn {
  models: GhcpModel[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch GHCP models for a given connection. Calls the runtime directly
 * (bypasses BPM). On failure the error is surfaced to the caller (shown as
 * a validation error on the field) and the model list is empty — the user
 * can still type any model name via freeform input.
 */
export function useGhcpModels(
  connectionName: string,
  enabled = true,
): UseGhcpModelsReturn {
  const { getOttoToken } = useAuth();
  const { data: runtimeDetails } = useRuntimeDetails();
  const hostName = runtimeDetails?.hostName;

  const query = useQuery<GhcpModel[], Error>({
    queryKey: ['ghcp-models', hostName, connectionName],
    queryFn: async () => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      if (!hostName) throw new Error('Runtime host not available');

      return listConnectionModels(token, connectionName, hostName);
    },
    enabled: enabled && !!connectionName && !!hostName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  return {
    models: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}
